import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender,
    type SortingState,
} from '@tanstack/react-table';
import { Button, EmptyState, ErrorState } from '@fiado/react-kit';
import { resolveErrorMessage } from '../../../../api/errorMapping';
import { platformApi } from '../../../../api/endpoints/platform';
import { ContentLoading } from '../../../../components/shared/ContentLoading';
import { Gate } from '../../../../components/permissions/Gate';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../../components/ui/table';
import { DataTableToolbar } from '../../../../components/shared/DataTableToolbar';
import { DataTablePagination } from '../../../../components/shared/DataTablePagination';
import {
    buildRoleColumns,
    ROLE_TABLE_HIDDEN_COLUMNS,
} from '../../../../components/roles/roleTableColumns';
import { useTenantLabelResolver } from '../../../../hooks/useTenantLabelResolver';
import { HelpHint } from '@/components/ui/HelpHint';
import { HELP_TEXT } from '@/lib/helpText';
import { globalFilterFn } from '../../../../lib/dataTable';
import { PlatformTenantRoleForm } from './PlatformTenantRoleForm';
import { PlatformTenantRoleRowActions } from './PlatformTenantRoleRowActions';

interface Props {
    /** tenantId del DRILL-DOWN (param de ruta). Toda la data se consume contra ESTE tenant. */
    tenantId: string;
}

/** Roles del tenant visto desde plataforma: mismas columnas que la tabla del tenant, con sus etiquetas. */
export function PlatformTenantRolesTable({ tenantId }: Props) {
    const [createOpen, setCreateOpen] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState<SortingState>([{ id: 'roleName', desc: false }]);

    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['platform', 'tenants', tenantId, 'roles'],
        queryFn: () => platformApi.listTenantRoles(tenantId),
        enabled: !!tenantId,
    });
    const labels = useTenantLabelResolver(tenantId);

    const columns = useMemo(
        () =>
            buildRoleColumns({
                labels,
                renderActions: (role, isSystem) => (
                    <PlatformTenantRoleRowActions
                        role={role}
                        tenantId={tenantId}
                        isSystem={isSystem}
                        labels={labels}
                    />
                ),
            }),
        [labels, tenantId],
    );

    const rows = useMemo(() => data?.items ?? [], [data]);
    const table = useReactTable({
        data: rows,
        columns,
        state: { globalFilter, sorting },
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        globalFilterFn,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize: 25 }, columnVisibility: ROLE_TABLE_HIDDEN_COLUMNS },
    });

    if (isLoading) return <ContentLoading />;
    if (isError) {
        return (
            <ErrorState
                title="No pudimos cargar los roles de la organización"
                message={resolveErrorMessage(error)}
                retryLabel="Reintentar"
                onRetry={() => {
                    void refetch();
                }}
            />
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex justify-end">
                <Gate permission="tenant.role.create" scope="TENANT" scopeRef={tenantId}>
                    <Button
                        onClick={() => {
                            setCreateOpen(true);
                        }}
                    >
                        Crear rol personalizado
                    </Button>
                </Gate>
            </div>

            {/* Modal plano: crear rol personalizado sin navegar de página. */}
            {createOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setCreateOpen(false);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="inline-flex items-center gap-1.5 text-lg font-semibold">
                                Crear rol personalizado
                                <HelpHint text={HELP_TEXT.customRole} />
                            </h2>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setCreateOpen(false);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <PlatformTenantRoleForm
                            tenantId={tenantId}
                            onDone={() => {
                                setCreateOpen(false);
                            }}
                        />
                    </div>
                </div>
            )}

            <DataTableToolbar
                value={globalFilter}
                onChange={setGlobalFilter}
                placeholder="Buscar por nombre…"
            />

            {rows.length === 0 ? (
                <EmptyState
                    title="Esta organización todavía no tiene roles"
                    description="Cuando crees un rol, aparecerá aquí."
                />
            ) : (
                <>
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((hg) => (
                                <TableRow key={hg.id}>
                                    {hg.headers.map((h) => (
                                        <TableHead key={h.id} className="text-center">
                                            {flexRender(h.column.columnDef.header, h.getContext())}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows.map((r) => (
                                <TableRow key={r.id}>
                                    {r.getVisibleCells().map((c) => (
                                        <TableCell key={c.id} className="text-center align-middle">
                                            {flexRender(c.column.columnDef.cell, c.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <DataTablePagination table={table} />
                </>
            )}
        </div>
    );
}
